export const JIELV_WORKER_SUPPLIER_SCOPES = ['jin_jiang', 'long_teng', 'sui_chuan', 'li_cheng']
export const JIELV_WORKER_SEGMENTS = ['recent', 'future', 'booking_recovery']

const supplierNames = { jin_jiang: '锦江', long_teng: '龙腾', sui_chuan: '隧穿亚朵', li_cheng: '丽呈' }
const segmentNames = { recent: '近期', future: '中远期', booking_recovery: '预订失败紧急补推' }

export const JIELV_WORKER_SUM_WARNING = '捷旅资源通道不使用总并发、比例或 QPS 分配；每个供应商的全量校准、近期、中远期与预订失败紧急补推仅由各自 Worker 数控制。'

export function jielvWorkerScopeName(scope, segment) {
  return `${supplierNames[scope] || scope || '-'}${segmentNames[segment] || segment || ''} Worker`
}

export function jielvWorkerScopeDescription(scope, segment) {
  if (!JIELV_WORKER_SUPPLIER_SCOPES.includes(scope) || !JIELV_WORKER_SEGMENTS.includes(segment)) return '-'
  return `仅控制${supplierNames[scope]}${segmentNames[segment]}资源队列的实际 Worker 数；不按比例分配，不受 QPS 字段限制，并与其他供应商和另一时间分段隔离。`
}

export function jielvWorkerScopeMax(scope, segment) {
  if (!JIELV_WORKER_SUPPLIER_SCOPES.includes(scope)) return 0
  return segment === 'booking_recovery' ? 64 : 512
}

export function jielvSupplierWorkerTotal(rows) {
  return (rows || []).reduce((sum, row) => JIELV_WORKER_SUPPLIER_SCOPES.includes(row.scope)
    ? sum + Number(row.full_worker_count || 0) + Number(row.recent_worker_count || 0) + Number(row.future_worker_count || 0) + Number(row.booking_recovery_worker_count || 0)
    : sum, 0)
}

export function jielvTotalWorkerCount(rows) {
  return jielvSupplierWorkerTotal(rows)
}

function laneWaiting(metric) {
  const lane = metric || {}
  return Math.max(0, Number(lane.waiting == null ? lane.ready : lane.waiting) || 0)
}

function laneConfiguredWorkers(metric, configuredWorkers) {
  const lane = metric || {}
  if (Object.prototype.hasOwnProperty.call(lane, 'configured_workers')) {
    const observed = Number(lane.configured_workers)
    if (Number.isFinite(observed) && observed >= 0) return observed
  }
  return Math.max(0, Number(configuredWorkers) || 0)
}

export function jielvLaneWorkerState(metric, configuredWorkers) {
  const lane = metric || {}
  const configured = Math.max(0, Number(configuredWorkers) || 0)
  const effective = laneConfiguredWorkers(lane, configuredWorkers)
  const stale = lane.heartbeat_stale === true || lane.worker_heartbeat_stale === true
  const online = stale ? 0 : Math.max(0, Number(lane.worker_processes) || 0)
  // processing comes from the live lease ZSET in the same backlog sample. It
  // is more current than the heartbeat's sampled actual_workers value.
  const busy = stale ? 0 : Math.max(0, Number(lane.processing) || 0)
  const idle = Math.max(0, online - busy)
  return {
    configured,
    effective,
    online,
    busy,
    idle,
    stale,
    utilization: online > 0 ? Math.min(100, Math.round(busy * 100 / online)) : 0,
    configMismatch: configured > 0 && effective !== configured,
  }
}

export function jielvLaneBacklogPerWorker(metric, configuredWorkers) {
  const lane = metric || {}
  const workers = laneConfiguredWorkers(lane, configuredWorkers)
  const waiting = laneWaiting(lane)
  return workers > 0 ? waiting / workers : waiting
}

// This is queue pressure rather than task completion. Keep it aligned with
// Daolv so operators can compare isolated recent/future/recovery lanes.
export function jielvLanePressurePercentage(metric, configuredWorkers) {
  return Math.min(100, Math.round(jielvLaneBacklogPerWorker(metric, configuredWorkers) * 10))
}

export function jielvLanePressureStatus(metric, configuredWorkers) {
  const lane = metric || {}
  const waiting = laneWaiting(lane)
  const heartbeatStale = lane.heartbeat_stale === true || lane.worker_heartbeat_stale === true
  const workerProcesses = Math.max(0, Number(lane.worker_processes) || 0)
  if (lane.monitor_verified === false) return 'exception'
  if (waiting > 0 && (heartbeatStale || workerProcesses <= 0)) return 'exception'
  if (Number(lane.oldest_wait_seconds || 0) > 600) return 'exception'
  if (Number(lane.blocked_master || 0) > 0 || Number(lane.failure_minute || 0) > 0) return 'warning'
  const perWorker = jielvLaneBacklogPerWorker(lane, configuredWorkers)
  if (perWorker > 5) return 'exception'
  if (perWorker > 1) return 'warning'
  return 'success'
}

export function jielvLanePressureText(metric, configuredWorkers) {
  return { success: '顺畅', warning: '关注', exception: '异常' }[jielvLanePressureStatus(metric, configuredWorkers)] || '未知'
}

export function jielvLaneDiagnosis(metric, configuredWorkers) {
  const lane = metric || {}
  const waiting = laneWaiting(lane)
  const processing = Math.max(0, Number(lane.processing) || 0)
  const delayed = Math.max(0, Number(lane.delayed_retry) || 0)
  const blocked = Math.max(0, Number(lane.blocked_master) || 0)
  const oldest = Math.max(0, Number(lane.oldest_wait_seconds) || 0)
  const failures = Math.max(0, Number(lane.failure_minute) || 0)
  const enqueued = Math.max(0, Number(lane.enqueued_minute) || 0)
  const consumed = Math.max(0, Number(lane.consumed_minute) || 0)
  const workers = jielvLaneWorkerState(lane, configuredWorkers)

  if (lane.monitor_verified === false) return { level: 'danger', title: '监控数据不完整', detail: '不能把缺失指标当作健康零值' }
  if (waiting > 0 && workers.stale) return { level: 'danger', title: 'Worker 心跳过期', detail: `${waiting} 个任务等待，当前没有可信在线 Worker` }
  if (waiting > 0 && workers.online <= 0) return { level: 'danger', title: 'Worker 离线', detail: `${waiting} 个任务等待，但在线 Worker 槽位为 0` }
  if (oldest > 600) return { level: 'danger', title: '等待超过 10 分钟', detail: `最老未领取任务已等待 ${oldest} 秒` }
  if (jielvLaneBacklogPerWorker(lane, configuredWorkers) > 5) return { level: 'danger', title: '积压超过处理能力', detail: `每个生效 Worker 对应 ${jielvLaneBacklogPerWorker(lane, configuredWorkers).toFixed(1)} 个等待任务` }
  if (workers.configMismatch) return { level: 'warning', title: '配置尚未完全生效', detail: `页面配置 ${workers.configured}，Go 生效 ${workers.effective}` }
  if (blocked > 0) return { level: 'warning', title: '主数据阻塞', detail: `${blocked} 家酒店需先完成酒店/房型/价格计划推送` }
  if (failures > 0) return { level: 'warning', title: '本分钟存在失败', detail: `${failures} 个任务失败，请查看失败摘要` }
  if (delayed > 0) return { level: 'warning', title: '存在延迟重试', detail: `${delayed} 个任务正在等待下一次重试` }
  if (waiting > 0 && enqueued > consumed) return { level: 'warning', title: '入队快于消费', detail: `本分钟入队 ${enqueued}，消费 ${consumed}` }
  if (oldest > 120 || jielvLaneBacklogPerWorker(lane, configuredWorkers) > 1) return { level: 'warning', title: '消费需要关注', detail: `${waiting} 个任务等待，最老等待 ${oldest} 秒` }
  if (processing > 0) return { level: 'success', title: '正在正常处理', detail: `${processing} 个任务执行中，Worker 利用率 ${workers.utilization}%` }
  if (waiting > 0) return { level: 'success', title: '等待领取', detail: `${waiting} 个任务处于正常可领取状态` }
  return { level: 'success', title: '队列顺畅', detail: '没有待领取、执行中或延迟重试任务' }
}

const hasOwn = (value, field) => Object.prototype.hasOwnProperty.call(value || {}, field)
const positiveInteger = (value, maximum) => Number.isInteger(Number(value)) && Number(value) > 0 && Number(value) <= maximum

// The Admin must never turn an old/partial API response into plausible-looking
// production values. Server-side defaults are authoritative and are returned
// as complete supplier rows; missing fields therefore mean the PHP endpoint is
// stale or incomplete and saving must be disabled until it is upgraded.
export function normalizeJielvWorkerConfig(data, definitions) {
  const payload = data || {}
  const source = Array.isArray(payload.suppliers) ? payload.suppliers : (Array.isArray(payload.items) ? payload.items : [])
  const issues = []
  const rows = (definitions || []).map(definition => {
    const item = source.find(value => value && value.scope === definition.scope)
    if (!item) {
      issues.push(`${definition.label}配置缺失`)
      return { ...definition, push_days: null, recent_days: null, full_worker_count: null, recent_worker_count: null, future_worker_count: null, booking_recovery_worker_count: null, enabled: false, config_source: 'missing', config_verified: false }
    }
    const fullWorkerField = hasOwn(item, 'full_worker_count') ? 'full_worker_count' : (hasOwn(item, 'worker_count') ? 'worker_count' : '')
    const recentWorkerField = hasOwn(item, 'recent_worker_count') ? 'recent_worker_count' : (hasOwn(item, 'worker_count') ? 'worker_count' : '')
    const required = [
      ['push_days', item.push_days, 366],
      ['recent_days', item.recent_days, 366],
      ['full_worker_count', fullWorkerField ? item[fullWorkerField] : null, 256],
      ['recent_worker_count', recentWorkerField ? item[recentWorkerField] : null, 512],
      ['future_worker_count', item.future_worker_count, 512],
      ['booking_recovery_worker_count', item.booking_recovery_worker_count, 64],
    ]
    const missing = required.filter(([, value, maximum]) => !positiveInteger(value, maximum)).map(([field]) => field)
    if (!hasOwn(item, 'enabled')) missing.push('enabled')
    if (positiveInteger(item.push_days, 366) && positiveInteger(item.recent_days, 366) && Number(item.recent_days) > Number(item.push_days)) missing.push('recent_days>push_days')
    if (item.recent_days_source !== 'supplier_resource_worker') missing.push('recent_days_source')
    if (missing.length) issues.push(`${definition.label}缺少或包含非法字段：${missing.join(', ')}`)
    return {
      ...definition,
      push_days: positiveInteger(item.push_days, 366) ? Number(item.push_days) : null,
      recent_days: positiveInteger(item.recent_days, 366) ? Number(item.recent_days) : null,
      recent_days_source: item.recent_days_source || '',
      full_worker_count: fullWorkerField && positiveInteger(item[fullWorkerField], 256) ? Number(item[fullWorkerField]) : null,
      recent_worker_count: recentWorkerField && positiveInteger(item[recentWorkerField], 512) ? Number(item[recentWorkerField]) : null,
      future_worker_count: positiveInteger(item.future_worker_count, 512) ? Number(item.future_worker_count) : null,
      booking_recovery_worker_count: positiveInteger(item.booking_recovery_worker_count, 64) ? Number(item.booking_recovery_worker_count) : null,
      enabled: hasOwn(item, 'enabled') ? item.enabled !== false && Number(item.enabled) !== 0 : false,
      config_source: item.config_source || 'database_legacy_response',
      updated_at: item.updated_at || null,
      config_verified: missing.length === 0,
    }
  })
  const policy = payload.execution_policy || {}
  if (policy.mode !== 'worker_only' || policy.qps_limit_enabled !== false) issues.push('后端未确认 Worker Only（无 QPS）执行策略')
  return { rows, issues, executionPolicy: policy, updatedAt: payload.updated_at || null }
}

const backlogNumberFields = [
  'ready', 'waiting', 'processing', 'delayed_retry', 'oldest_wait_seconds',
  'actual_workers', 'configured_workers', 'worker_processes',
]

const emptyBacklogLane = () => ({
  ready: 0,
  waiting: 0,
  processing: 0,
  delayed_retry: 0,
  oldest_wait_seconds: 0,
  actual_workers: 0,
  configured_workers: 0,
  worker_processes: 0,
  heartbeat_stale: true,
  monitor_verified: false,
})

const emptyFullCalibration = () => ({
  status: 'unknown',
  authorized_hotels: 0,
  processed_hotels: 0,
  remaining_hotels: 0,
  progress_percent: 0,
  succeeded: 0,
  failed: 0,
  retry_hotels: 0,
  monitor_verified: false,
})

// Backlog metrics are operational evidence. Missing suppliers or lanes must not
// be rendered as healthy zeroes after a partial/stale PHP deployment.
export function normalizeJielvBacklog(data, definitions) {
  const payload = data || {}
  const source = Array.isArray(payload.suppliers) ? payload.suppliers : []
  const issues = []
  if (payload.status === 'unavailable') issues.push(payload.message || 'Redis 积压暂时不可用')
  if (!payload.sampled_at) issues.push('积压采样时间缺失')

  const rows = (definitions || []).map(definition => {
    const item = source.find(value => value && value.scope === definition.scope)
    if (!item) {
      issues.push(`${definition.label}积压数据缺失`)
      return {
        scope: definition.scope,
        recent: emptyBacklogLane(),
        future: emptyBacklogLane(),
        booking_recovery: emptyBacklogLane(),
        full: emptyFullCalibration(),
        monitor_verified: false,
      }
    }
    let verified = true
    const row = { ...item }
    JIELV_WORKER_SEGMENTS.forEach(segment => {
      const lane = item[segment]
      const missing = !lane
        ? ['lane']
        : backlogNumberFields.filter(field => !hasOwn(lane, field) || !Number.isFinite(Number(lane[field]))).concat(hasOwn(lane, 'heartbeat_stale') || hasOwn(lane, 'worker_heartbeat_stale') ? [] : ['heartbeat_stale'])
      if (missing.length) {
        verified = false
        issues.push(`${definition.label}${segmentNames[segment]}监控字段缺失：${missing.join(', ')}`)
      }
      row[segment] = { ...emptyBacklogLane(), ...(lane || {}), monitor_verified: missing.length === 0 }
    })
    const full = item.full
    const fullFields = ['status', 'authorized_hotels', 'processed_hotels', 'remaining_hotels', 'progress_percent', 'succeeded', 'failed', 'retry_hotels']
    const fullMissing = !full ? ['full'] : fullFields.filter(field => !hasOwn(full, field))
    if (fullMissing.length) {
      verified = false
      issues.push(`${definition.label}每日校准监控字段缺失：${fullMissing.join(', ')}`)
    }
    row.full = { ...emptyFullCalibration(), ...(full || {}), monitor_verified: fullMissing.length === 0 }
    row.monitor_verified = verified
    return row
  })
  return {
    rows,
    issues: [...new Set(issues)],
    sampledAt: payload.sampled_at || null,
    status: payload.status || 'unknown',
  }
}
